import { Component, Input, inject } from '@angular/core';
import { firstValueFrom, take } from 'rxjs';
import { CakeGroup, CakeTopologyBidirectionalDirection, CakeTopologyStateSnapshot, EfCakeTopologySaveRequest, EfCakeTopologyUiState } from '../../../shared/models/ef-cake-topology.model';
import { EfateCakeTopologyApiService } from '../../../shared/services/efate-cake/efate-cake-topology.api.service';
import { EFateConstants } from '../../../shared/utils/efate-constants';
import { EfCakeTopologySharedService } from '../../shared/services/ef-cake-topology-shared.service';
import { EfCakeTopologyStateUtils } from '../../shared/utils/ef-cake-topology-state.utils';

type EdgeStyle = 'solid' | 'dashed';
type BidirectionalDirection = CakeTopologyBidirectionalDirection;

@Component({
  selector: 'app-ef-cake-topology',
  templateUrl: './ef-cake-topology.component.html',
  styleUrls: ['./ef-cake-topology.component.css']
})
export class EFCakeTopologyComponent {
  private readonly efateCakeTopologyApiService = inject(EfateCakeTopologyApiService);
  private readonly efCakeTopologySharedService = inject(EfCakeTopologySharedService);

  @Input() readonlyMode: boolean = false;

  readonly maxNodeNameLength = EFateConstants.MAX_NODE_NAME_LENGTH;
  readonly maxGroupNameLength = EFateConstants.MAX_GROUP_NAME_LENGTH;
  readonly maxCakeGroups = EFateConstants.MAX_CAKE_GROUPS;
  readonly groupColors = EFateConstants.GROUP_COLORS;
  readonly topologyNodeKeys: string[] = EFateConstants.TOPOLOGY_NODE_KEYS;
  nodeNames: Record<string, string> = { ...EFateConstants.DEFAULT_NODE_NAMES };
  cakeGroups: CakeGroup[] = [
    { id: 1, name: `${EFateConstants.DEFAULT_GROUP_NAME_PREFIX} 1` }
  ];
  selectedCakeGroupId = 1;

  private topologyByGroup: Record<string, CakeTopologyStateSnapshot> = {
    [EFateConstants.DEFAULT_GROUP_KEY]: EFateConstants.createDefaultSnapshot()
  };

  activeEdges = new Set<string>();
  activeStatusNodes = new Set<string>(EFateConstants.DEFAULT_ACTIVE_STATUS_NODES);
  bidirectionalEdgeDirections: Record<string, BidirectionalDirection> = { ...EFateConstants.DEFAULT_BIDIRECTIONAL_EDGE_DIRECTIONS };

  toggleSingleDirectionEdge(edgeId: string): void {
    if (this.readonlyMode) {
      return;
    }

    if (this.activeEdges.has(edgeId)) {
      this.activeEdges.delete(edgeId);
      this.normalizeCakeTopologyState();
      return;
    }

    if (!this.isEdgeEnabled(edgeId)) {
      return;
    }

    this.activeEdges.add(edgeId);
    this.enableDefaultStatusNodes();
  }

  toggleBidirectionalEdge(edgeId: string): void {
    if (this.readonlyMode) {
      return;
    }

    const availableDirections = this.getAvailableBidirectionalDirections(edgeId);
    if (!availableDirections.length) {
      return;
    }

    const currentDirection = this.bidirectionalEdgeDirections[edgeId] ?? EFateConstants.BIDIRECTIONAL_EDGE_DIRECTIONS.NONE;

    if (availableDirections.length === 1) {
      const onlyDirection = availableDirections[0];
      this.bidirectionalEdgeDirections[edgeId] = currentDirection === onlyDirection ? EFateConstants.BIDIRECTIONAL_EDGE_DIRECTIONS.NONE : onlyDirection;
      this.normalizeCakeTopologyState();
      return;
    }

    if (currentDirection === EFateConstants.BIDIRECTIONAL_EDGE_DIRECTIONS.NONE) {
      this.bidirectionalEdgeDirections[edgeId] = EFateConstants.BIDIRECTIONAL_EDGE_DIRECTIONS.FORWARD;
    } else if (currentDirection === EFateConstants.BIDIRECTIONAL_EDGE_DIRECTIONS.FORWARD) {
      this.bidirectionalEdgeDirections[edgeId] = EFateConstants.BIDIRECTIONAL_EDGE_DIRECTIONS.REVERSE;
    } else {
      this.bidirectionalEdgeDirections[edgeId] = EFateConstants.BIDIRECTIONAL_EDGE_DIRECTIONS.NONE;
    }

    this.normalizeCakeTopologyState();
  }

  isEdgeActive(edgeId: string): boolean {
    return this.activeEdges.has(edgeId);
  }

  isBidirectionalEdgeActive(edgeId: string): boolean {
    return (this.bidirectionalEdgeDirections[edgeId] ?? EFateConstants.BIDIRECTIONAL_EDGE_DIRECTIONS.NONE) !== EFateConstants.BIDIRECTIONAL_EDGE_DIRECTIONS.NONE;
  }

  isEdgeEnabled(edgeId: string): boolean {
    const sourceNode = EFateConstants.EDGE_TO_SOURCE_NODE[edgeId];
    if (!sourceNode || sourceNode === EFateConstants.DEFAULT_NODE_NAMES.Parent) {
      return true;
    }

    return this.isNodeActivated(sourceNode);
  }

  isBidirectionalEdgeEnabled(edgeId: string): boolean {
    return this.getAvailableBidirectionalDirections(edgeId).length > 0;
  }

  getEdgeMarker(edgeId: string, style: EdgeStyle): string {
    if (!this.isEdgeActive(edgeId)) {
      return style === 'solid' ? 'url(#arrow-solid)' : 'url(#arrow-dashed)';
    }

    return style === 'solid' ? 'url(#arrow-solid-active)' : 'url(#arrow-dashed-active)';
  }

  getBidirectionalMarkerStart(edgeId: string): string | null {
    const direction = this.bidirectionalEdgeDirections[edgeId] ?? EFateConstants.BIDIRECTIONAL_EDGE_DIRECTIONS.NONE;
    if (direction === EFateConstants.BIDIRECTIONAL_EDGE_DIRECTIONS.NONE) {
      return 'url(#arrow-dashed)';
    }

    if (direction === EFateConstants.BIDIRECTIONAL_EDGE_DIRECTIONS.REVERSE) {
      return 'url(#arrow-dashed-active)';
    }

    return null;
  }

  getBidirectionalMarkerEnd(edgeId: string): string | null {
    const direction = this.bidirectionalEdgeDirections[edgeId] ?? EFateConstants.BIDIRECTIONAL_EDGE_DIRECTIONS.NONE;
    if (direction === EFateConstants.BIDIRECTIONAL_EDGE_DIRECTIONS.NONE) {
      return 'url(#arrow-dashed)';
    }

    if (direction === EFateConstants.BIDIRECTIONAL_EDGE_DIRECTIONS.FORWARD) {
      return 'url(#arrow-dashed-active)';
    }

    return null;
  }

  getNodeClass(nodeId: string): string {
    return this.isNodeActivated(nodeId) ? 'node-active' : 'node-default';
  }

  getNodeName(nodeId: string): string {
    return this.nodeNames[nodeId] || nodeId;
  }

  getNodeDisplayName(nodeId: string): string {
    const value = this.getNodeName(nodeId);
    const maxVisibleLength = EFateConstants.NODE_LABEL_MAX_VISIBLE_LENGTH;

    if (value.length <= maxVisibleLength) {
      return value;
    }

    return `${value.slice(0, maxVisibleLength - 1)}…`;
  }

  normalizeNodeName(nodeKey: string): void {
    const currentName = this.nodeNames[nodeKey];
    if (typeof currentName !== 'string') {
      this.nodeNames[nodeKey] = nodeKey;
      return;
    }

    const normalizedName = currentName.trim().slice(0, this.maxNodeNameLength);
    this.nodeNames[nodeKey] = normalizedName || nodeKey;
  }

  addCakeGroup(): void {
    if (this.readonlyMode) {
      return;
    }

    if (this.cakeGroups.length >= this.maxCakeGroups) {
      return;
    }

    this.saveCurrentGroupState();

    const nextIndex = this.cakeGroups.length + 1;
    const newGroup = {
      id: Date.now() + Math.random(),
      name: `${EFateConstants.DEFAULT_GROUP_NAME_PREFIX} ${nextIndex}`
    };

    const groupKey = this.getGroupKeyByIndex(nextIndex - 1);
    this.cakeGroups.push(newGroup);
    this.topologyByGroup[groupKey] = EFateConstants.createDefaultSnapshot();
    this.selectedCakeGroupId = newGroup.id;
    this.restoreGroupState(this.selectedCakeGroupId);
  }

  removeCakeGroup(groupId: number): void {
    if (this.readonlyMode) {
      return;
    }

    if (this.cakeGroups.length <= 1) {
      return;
    }

    this.saveCurrentGroupState();
    const removedGroupIndex = this.getGroupIndexById(groupId);
    const previousTopologyByGroup = { ...this.topologyByGroup };
    this.cakeGroups = this.cakeGroups.filter(group => group.id !== groupId);
    this.reindexTopologySnapshotsAfterRemoval(removedGroupIndex, previousTopologyByGroup);

    if (this.selectedCakeGroupId === groupId) {
      this.selectedCakeGroupId = this.cakeGroups[0].id;
    }

    this.restoreGroupState(this.selectedCakeGroupId);
  }

  selectCakeGroup(groupId: number): void {
    if (this.readonlyMode) {
      return;
    }

    if (this.selectedCakeGroupId === groupId) {
      return;
    }

    this.saveCurrentGroupState();
    this.selectedCakeGroupId = groupId;
    this.restoreGroupState(groupId);
  }

  getSelectedCakeGroupName(): string {
    const selectedGroup = this.cakeGroups.find(group => group.id === this.selectedCakeGroupId);
    return selectedGroup?.name || `${EFateConstants.DEFAULT_GROUP_NAME_PREFIX} 1`;
  }

  getGroupColorForGroup(groupId: number): string {
    const groupIndex = this.cakeGroups.findIndex(group => group.id === groupId);
    return this.groupColors[groupIndex >= 0 ? groupIndex : 0] ?? this.groupColors[0];
  }

  getSelectedGroupColor(): string {
    const groupIndex = this.cakeGroups.findIndex(group => group.id === this.selectedCakeGroupId);
    return this.groupColors[groupIndex >= 0 ? groupIndex : 0] ?? this.groupColors[0];
  }

  selectFirstCakeGroup(): void {
    if (this.cakeGroups.length === 0) {
      return;
    }

    this.selectedCakeGroupId = this.cakeGroups[0].id;
    this.restoreGroupState(this.selectedCakeGroupId);
  }

  normalizeCakeGroupName(group: CakeGroup): void {
    if (this.readonlyMode) {
      return;
    }

    const trimmed = (group.name ?? '').trim();
    const index = this.getGroupIndexById(group.id) + 1;
    const fallbackName = `${EFateConstants.DEFAULT_GROUP_NAME_PREFIX} ${index || 1}`;
    group.name = trimmed.slice(0, this.maxGroupNameLength) || fallbackName;
  }

  trackCakeGroup(index: number, group: CakeGroup): number {
    return group.id;
  }

  toggleStatusNode(statusNodeId: string): void {
    if (this.readonlyMode) {
      return;
    }

    if (!this.isStatusNodeEnabled(statusNodeId)) {
      return;
    }

    if (this.activeStatusNodes.has(statusNodeId)) {
      this.activeStatusNodes.delete(statusNodeId);
      return;
    }

    this.activeStatusNodes.add(statusNodeId);
  }

  isStatusNodeEnabled(statusNodeId: string): boolean {
    const ownerNodeId = EFateConstants.STATUS_NODE_TO_OWNER_NODE[statusNodeId];
    if (!ownerNodeId) {
      return false;
    }

    return this.isNodeActivated(ownerNodeId);
  }

  getStatusNodeClass(statusNodeId: string): string {
    return this.activeStatusNodes.has(statusNodeId) ? 'status-active' : 'status-disabled';
  }

  saveCurrentGroupState(): void {
    const currentGroupKey = this.getGroupKeyById(this.selectedCakeGroupId);

    this.topologyByGroup[currentGroupKey] = {
      activeEdges: Array.from(this.activeEdges),
      bidirectionalEdges: { ...this.bidirectionalEdgeDirections },
      activeStatusNodes: Array.from(this.activeStatusNodes)
    };
  }

  buildCakeTopologySavePayload(): any {
    const groups = this.cakeGroups.map((group, index) => ({
      groupKey: this.getGroupKeyByIndex(index),
      groupName: group.name.trim() || `${EFateConstants.DEFAULT_GROUP_NAME_PREFIX} ${index + 1}`,
      topologyKey: EFateConstants.DEFAULT_TOPOLOGY_KEY
    }));

    const topologyByGroup = Object.entries(this.topologyByGroup).reduce((acc, [groupKey, groupState]) => {
      acc[groupKey] = {
        activeEdges: [...(groupState?.activeEdges ?? [])],
        bidirectionalEdges: { ...(groupState?.bidirectionalEdges ?? {}) },
        activeStatusNodes: [...(groupState?.activeStatusNodes ?? [])]
      };
      return acc;
    }, {} as Record<string, CakeTopologyStateSnapshot>);

    const payload = {
      nodeNames: { ...this.nodeNames },
      groups,
      topologyByGroup
    };

    return payload;
  }

  logCakeTopologySavePayload(): any {
    this.saveCurrentGroupState();
    const payload = this.buildCakeTopologySavePayload();
    console.log(EFateConstants.PAYLOAD_LOG_LABEL, JSON.stringify(payload, null, 2));
    return payload;
  }

  async loadCakeTopologyPayloadBySetup(efateSetupPk?: number): Promise<boolean> {
    const topologyState = await this.efCakeTopologySharedService.getCakeTopologyStateBySetupPk(efateSetupPk);
    this.applyCakeTopologyState(topologyState);
    return !!topologyState;
  }

  public applyCakeTopologyState(topologyState?: EfCakeTopologyUiState, targetGroupKey?: string): void {
    if (!topologyState) {
      this.resetCakeTopologyToDefaultState();
      return;
    }

    const restoredGroups = topologyState.groups.map((group, index) => ({
      id: index + 1,
      name: group.groupName || `${EFateConstants.DEFAULT_GROUP_NAME_PREFIX} ${index + 1}`
    }));

    this.cakeGroups = restoredGroups.length > 0
      ? restoredGroups
      : [{ id: 1, name: `${EFateConstants.DEFAULT_GROUP_NAME_PREFIX} 1` }];
    this.nodeNames = { ...EFateConstants.DEFAULT_NODE_NAMES, ...(topologyState.nodeNames ?? {}) };

    const normalizedTopologyByGroup = this.cakeGroups.reduce((acc, _group, index) => {
      const groupKey = this.getGroupKeyByIndex(index);
      acc[groupKey] = topologyState.topologyByGroup[groupKey] ?? EFateConstants.createDefaultSnapshot();
      return acc;
    }, {} as Record<string, CakeTopologyStateSnapshot>);

    this.topologyByGroup = normalizedTopologyByGroup;

    const requestedGroupKey = targetGroupKey || this.getGroupKeyByIndex(0);
    const requestedGroupIndex = requestedGroupKey
      ? this.cakeGroups.findIndex((_group, index) => this.getGroupKeyByIndex(index) === requestedGroupKey)
      : -1;

    this.selectedCakeGroupId = requestedGroupIndex >= 0
      ? this.cakeGroups[requestedGroupIndex].id
      : (this.cakeGroups[0]?.id ?? 1);

    this.restoreGroupState(this.selectedCakeGroupId);
  }

  async saveCakeTopologyPayload(efateSetupPk?: number): Promise<any> {
    this.saveCurrentGroupState();
    const payload = this.buildCakeTopologySavePayload();

    if (!efateSetupPk || efateSetupPk <= 0) {
      console.warn('Cake topology save skipped because efateSetupPk is missing.');
      return payload;
    }

    const request: EfCakeTopologySaveRequest = {
      efateSetupPk,
      topologyJson: JSON.stringify(payload)
    };

    try {
      const response = await firstValueFrom(this.efateCakeTopologyApiService.saveEfateCakeTopology(request).pipe(take(1)));
      console.log('Cake topology saved successfully', response);
      return response;
    } catch (error) {
      console.warn('Error saving cake topology payload', error);
      return payload;
    }
  }

  private resetCakeTopologyToDefaultState(): void {
    this.cakeGroups = [{ id: 1, name: `${EFateConstants.DEFAULT_GROUP_NAME_PREFIX} 1` }];
    this.selectedCakeGroupId = 1;
    this.nodeNames = { ...EFateConstants.DEFAULT_NODE_NAMES };
    this.topologyByGroup = {
      [EFateConstants.DEFAULT_GROUP_KEY]: EFateConstants.createDefaultSnapshot()
    };
    this.activeEdges = new Set<string>();
    this.activeStatusNodes = new Set<string>(EFateConstants.DEFAULT_ACTIVE_STATUS_NODES);
    this.bidirectionalEdgeDirections = { ...EFateConstants.DEFAULT_BIDIRECTIONAL_EDGE_DIRECTIONS };
  }

  private restoreGroupState(groupId: number): void {
    const currentGroupKey = this.getGroupKeyById(groupId);
    const snapshot = this.topologyByGroup[currentGroupKey] ?? EFateConstants.createDefaultSnapshot();

    this.activeEdges = new Set(snapshot.activeEdges);
    this.activeStatusNodes = new Set(snapshot.activeStatusNodes);
    this.bidirectionalEdgeDirections = {
      ...EFateConstants.DEFAULT_BIDIRECTIONAL_EDGE_DIRECTIONS,
      ...snapshot.bidirectionalEdges,
    };
  }

  private isNodeActivated(nodeId: string): boolean {
    return EfCakeTopologyStateUtils.isNodeActivated(nodeId, this.activeEdges, this.bidirectionalEdgeDirections);
  }

  private getAvailableBidirectionalDirections(edgeId: string): BidirectionalDirection[] {
    const nodes = EFateConstants.BIDIRECTIONAL_EDGE_NODES[edgeId];
    if (!nodes) {
      return [];
    }

    const directions: BidirectionalDirection[] = [];
    if (this.isNodeActivated(nodes.from)) {
      directions.push(EFateConstants.BIDIRECTIONAL_EDGE_DIRECTIONS.FORWARD);
    }

    if (this.isNodeActivated(nodes.to)) {
      directions.push(EFateConstants.BIDIRECTIONAL_EDGE_DIRECTIONS.REVERSE);
    }

    return directions;
  }

  private normalizeCakeTopologyState(): void {
    let changed = false;

    do {
      changed = false;

      for (const edgeId of Array.from(this.activeEdges)) {
        if (!this.isEdgeEnabled(edgeId)) {
          this.activeEdges.delete(edgeId);
          changed = true;
        }
      }

      for (const edgeId of Object.keys(this.bidirectionalEdgeDirections)) {
        const direction = this.bidirectionalEdgeDirections[edgeId] ?? EFateConstants.BIDIRECTIONAL_EDGE_DIRECTIONS.NONE;
        if (direction === EFateConstants.BIDIRECTIONAL_EDGE_DIRECTIONS.NONE) {
          continue;
        }

        const availableDirections = this.getAvailableBidirectionalDirections(edgeId);
        if (!availableDirections.includes(direction)) {
          this.bidirectionalEdgeDirections[edgeId] = EFateConstants.BIDIRECTIONAL_EDGE_DIRECTIONS.NONE;
          changed = true;
        }
      }
    } while (changed);

    this.removeInvalidStatusNodes();
  }

  private removeInvalidStatusNodes(): void {
    for (const statusNodeId of Array.from(this.activeStatusNodes)) {
      if (!this.isStatusNodeEnabled(statusNodeId)) {
        this.activeStatusNodes.delete(statusNodeId);
      }
    }

    this.enableDefaultStatusNodes();
  }

  private enableDefaultStatusNodes(): void {
    for (const [statusNodeId, ownerNodeId] of Object.entries(EFateConstants.STATUS_NODE_TO_OWNER_NODE)) {
      if (this.isNodeActivated(ownerNodeId)) {
        this.activeStatusNodes.add(statusNodeId);
      }
    }
  }

  private getGroupIndexById(groupId: number): number {
    return this.cakeGroups.findIndex(group => group.id === groupId);
  }

  private getGroupKeyByIndex(index: number): string {
    const normalizedIndex = index >= 0 ? index + 1 : 1;
    return `${EFateConstants.GROUP_KEY_PREFIX}${normalizedIndex}`;
  }

  private getGroupKeyById(groupId: number): string {
    const groupIndex = this.getGroupIndexById(groupId);
    return this.getGroupKeyByIndex(groupIndex);
  }

  private reindexTopologySnapshotsAfterRemoval(removedGroupIndex: number, previousTopologyByGroup: Record<string, CakeTopologyStateSnapshot>): void {
    const reindexedTopologyByGroup: Record<string, CakeTopologyStateSnapshot> = {};

    this.cakeGroups.forEach((_, index) => {
      const oldIndex = removedGroupIndex >= 0 && index >= removedGroupIndex ? index + 1 : index;
      const oldGroupKey = this.getGroupKeyByIndex(oldIndex);
      const newGroupKey = this.getGroupKeyByIndex(index);
      reindexedTopologyByGroup[newGroupKey] = previousTopologyByGroup[oldGroupKey] ?? EFateConstants.createDefaultSnapshot();
    });

    this.topologyByGroup = reindexedTopologyByGroup;
  }
}
